'use client';

import * as React from 'react';

import { ChevronDown, Loader2, X } from 'lucide-react';

import { IGetParams } from '@/api/types';

import { Button } from '../../base/Button';
import { Input } from '../../base/Input';
import { cn } from '../../lib/utils';

export interface TwAsyncAutocompleteOption {
  _id?: string;
  [key: string]: any;
}

export interface TwAsyncAutocompleteProps<T extends TwAsyncAutocompleteOption> {
  /** API function to fetch data with pagination and search */
  fetchDataApi: (params: IGetParams) => Promise<{
    data: { results: T[]; count: number };
  }>;
  /** Function to extract display label from option */
  getLabel: (option: T) => string;
  /** Callback when an option is selected */
  onChange: (value: T) => void;
  /** Placeholder text for the input */
  placeholder?: string;
  /** Whether the component is disabled */
  disabled?: boolean;
  /** Scroll threshold in pixels before loading more items */
  scrollThreshold?: number;
  /** Number of items to fetch per page */
  dataLengthPerPage?: number;
  /** Additional CSS classes */
  className?: string;
  /** Optional icon to display on the left side of the input */
  icon?: React.ReactNode;
  /** Debounce time for search input in milliseconds */
  debounceTime?: number;
  /** Whether to display as a combobox with dropdown arrow */
  combobox?: boolean;
  /** Optional callback when fetch fails */
  onError?: (error: Error) => void;
  /** External controlled value - overrides internal state when set */
  value?: T | null;
  /** Initial value when component mounts */
  defaultValue?: T | null;
  /** When true, input is only for searching - selected value is never displayed in input */
  searchOnlyMode?: boolean;
}

export function TwAsyncAutocomplete<T extends TwAsyncAutocompleteOption>({
  fetchDataApi,
  getLabel,
  onChange,
  placeholder = 'Search...',
  disabled = false,
  scrollThreshold = 0,
  dataLengthPerPage = 100,
  className,
  icon,
  debounceTime = 500,
  combobox = false,
  onError,
  value = null,
  defaultValue = null,
  searchOnlyMode = false,
}: TwAsyncAutocompleteProps<T>) {
  const [open, setOpen] = React.useState(false);
  const [inputValue, setInputValue] = React.useState('');
  const [searchText, setSearchText] = React.useState('');
  const [page, setPage] = React.useState(1);
  const [count, setCount] = React.useState(0);
  const [loading, setLoading] = React.useState(false);
  const [options, setOptions] = React.useState<T[]>([]);
  const [focusedIndex, setFocusedIndex] = React.useState(-1);

  const inputRef = React.useRef<HTMLInputElement>(null);
  const dropdownRef = React.useRef<HTMLDivElement>(null);
  const listRef = React.useRef<HTMLDivElement>(null);
  const itemRefs = React.useRef<(HTMLButtonElement | null)[]>([]);
  const debounceTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const userClearedRef = React.useRef(false);

  // Fetch data when page or searchText changes
  React.useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const params: IGetParams = {
          limit: dataLengthPerPage,
          page,
          search: searchText,
        };
        const res = await fetchDataApi(params);
        setCount(res.data.count);
        setOptions((prev) =>
          page === 1 ? res.data.results : [...prev, ...res.data.results],
        );
      } catch (err: any) {
        onError?.(err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [page, searchText, fetchDataApi, dataLengthPerPage]);

  // Debounced input change handler
  const handleInputChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const valueInput = e.target.value;
      setInputValue(valueInput);
      userClearedRef.current = false;

      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      debounceTimerRef.current = setTimeout(() => {
        setPage(1);
        setSearchText(valueInput || '');
        setOpen(true);
      }, debounceTime);
    },
    [debounceTime],
  );

  // Handle scroll for infinite loading
  const handleScroll = React.useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
      if (
        scrollTop + clientHeight >= scrollHeight - scrollThreshold &&
        !loading &&
        options.length < count
      ) {
        setPage((prev) => prev + 1);
      }
    },
    [loading, options.length, count, scrollThreshold],
  );

  // Clear input
  const handleClear = React.useCallback(() => {
    setInputValue('');
    setSearchText('');
    setPage(1);
    setOpen(true);
    onChange(null as any);
    userClearedRef.current = true;
  }, [onChange]);

  // Handle option selection
  const handleSelect = React.useCallback(
    (option: T) => {
      onChange(option);
      setOpen(false);
      inputRef.current?.blur();
      userClearedRef.current = false;

      if (searchOnlyMode) {
        // In search-only mode, always clear input after selection
        setInputValue('');
        setSearchText('');
        setPage(1);
      } else {
        // Normal mode: show selected value label
        const label = getLabel(option);
        setInputValue(label);
        handleClear();
      }
    },
    [onChange, getLabel, handleClear, searchOnlyMode],
  );

  // Keyboard navigation for input
  const handleInputKeyDown = React.useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (!open || options.length === 0) return;

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setFocusedIndex(0);
        itemRefs.current[0]?.focus();
      } else if (e.key === 'Escape') {
        setOpen(false);
      } else if (e.key === 'Enter' && focusedIndex >= 0) {
        e.preventDefault();
        handleSelect(options[focusedIndex]);
      }
    },
    [open, options, focusedIndex, handleSelect],
  );

  // Keyboard navigation for dropdown items
  const handleItemKeyDown = React.useCallback(
    (e: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        const nextIndex = Math.min(index + 1, options.length - 1);
        setFocusedIndex(nextIndex);
        itemRefs.current[nextIndex]?.focus();
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        if (index === 0) {
          setFocusedIndex(-1);
          inputRef.current?.focus();
        } else {
          const prevIndex = Math.max(index - 1, 0);
          setFocusedIndex(prevIndex);
          itemRefs.current[prevIndex]?.focus();
        }
      } else if (e.key === 'Escape') {
        e.preventDefault();
        setOpen(false);
        inputRef.current?.focus();
      } else if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleSelect(options[index]);
      }
    },
    [options, handleSelect],
  );

  // Reset focused index when options change
  React.useEffect(() => {
    setFocusedIndex(-1);
    itemRefs.current = itemRefs.current.slice(0, options.length);
  }, [options]);

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(target) &&
        inputRef.current &&
        !inputRef.current.contains(target)
      ) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Cleanup debounce timer
  React.useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  // Sync inputValue when external value prop changes
  React.useEffect(() => {
    // In search-only mode, never sync selected value or defaultValue to input
    // Input should remain empty and only used for searching
    if (searchOnlyMode) {
      return;
    }

    // Don't sync if user cleared the value (prevent external value from overriding)
    if (userClearedRef.current && value !== null) {
      return;
    }

    if (value) {
      setInputValue(getLabel(value));
      userClearedRef.current = false;
    } else if (defaultValue) {
      setInputValue(getLabel(defaultValue));
    }
  }, [value, defaultValue, getLabel, searchOnlyMode]);

  const hasResults = options.length > 0;
  const hasIcon = Boolean(icon);

  return (
    <div className={cn('relative w-full', className)}>
      <div className="relative">
        {hasIcon && (
          <div className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground">
            {icon}
          </div>
        )}
        <Input
          ref={inputRef}
          type="text"
          placeholder={placeholder}
          disabled={disabled}
          className={cn(
            'h-9 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 pr-8',
            hasIcon && 'pl-8',
          )}
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleInputKeyDown}
          onFocus={() => setOpen(true)}
        />
        <div className="absolute right-0 top-0 h-full flex items-center pr-2 gap-1">
          {loading ? (
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          ) : (
            <>
              {inputValue && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-full px-1 py-0"
                  onClick={handleClear}
                  type="button"
                >
                  <X className="h-4 w-4" />
                  <span className="sr-only">Clear</span>
                </Button>
              )}
              {combobox && (
                <ChevronDown className="h-4 w-4 text-muted-foreground opacity-50" />
              )}
            </>
          )}
        </div>
      </div>

      {open && (hasResults || loading) && (
        <div
          ref={dropdownRef}
          className="absolute z-50 mt-1 w-full rounded-md border bg-popover text-popover-foreground shadow-md outline-none animate-in fade-in-0 zoom-in-95"
        >
          <div
            ref={listRef}
            className="max-h-[300px] overflow-auto p-1"
            onScroll={handleScroll}
          >
            {options.map((option, index) => (
              <button
                key={option._id || index}
                ref={(el) => {
                  itemRefs.current[index] = el;
                }}
                className={cn(
                  'w-full rounded-sm px-2 py-2 text-sm text-left truncate border-b border-gray-100 last:border-b-0',
                  focusedIndex === index
                    ? 'bg-accent text-accent-foreground'
                    : 'hover:bg-accent hover:text-accent-foreground',
                )}
                onClick={() => handleSelect(option)}
                onKeyDown={(e) => handleItemKeyDown(e, index)}
                type="button"
                title={getLabel(option)}
              >
                {getLabel(option)}
              </button>
            ))}
            {loading && (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                <span className="ml-2 text-sm text-muted-foreground">
                  Loading...
                </span>
              </div>
            )}
            {!loading && !hasResults && searchText && (
              <div className="flex items-center justify-center py-4 text-sm text-muted-foreground">
                No results found
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

TwAsyncAutocomplete.displayName = 'TwAsyncAutocomplete';
